/**
 * Listagem de orçamentos e pedidos.
 *
 * Existe porque criar um documento que não se consegue reabrir é produto
 * quebrado — e ninguém decora um UUID.
 */
import { useCallback, useEffect, useState } from "react";
import { api, type Filial } from "../api.ts";
import { BotaoImprimir, Impresso, situacaoImpressa } from "../componentes/Impresso.tsx";

interface LinhaOrcamento {
  id: string; numero: number; status: string; regra_cubagem: string;
  volume_total: string; valor_total: string; criado_em: string;
  parceiro: string; itens: string; pedido_numero: number | null;
}

interface LinhaPedido {
  id: string; numero: number; status: string;
  volume_total: string; valor_total: string; criado_em: string;
  parceiro: string; itens: string; orcamento_numero: number | null;
}

interface ItemDocumento {
  ordem: number; espMm: number | null; largMm: number | null; compM: string | null;
  pecas: number; volumeM3: string; precoUnitario: string; valorTotal: string;
}

const fmtVol = (v: string) =>
  Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtRs = (v: string) =>
  Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtData = (v: string) => new Date(v).toLocaleDateString("pt-BR");

function pilStatus(s: string) {
  const classe =
    s === "CONVERTIDO" || s === "FATURADO" ? "p-ok"
    : s === "ABERTO" ? "p-info"
    : s === "CANCELADO" ? "p-erro"
    : "p-aten";
  return <span className={`pil ${classe}`}><i />{s.toLowerCase()}</span>;
}

/* ---------------------------------------------------------- orçamentos */

export function Orcamentos({
  aviso, aoDuplicar, aoNovo,
}: {
  aviso: (t: string) => void;
  aoDuplicar: (itens: ItemDocumento[], parceiroId: string) => void;
  aoNovo: () => void;
}) {
  const [lista, setLista] = useState<LinhaOrcamento[] | null>(null);
  const [status, setStatus] = useState("");
  const [aberto, setAberto] = useState<string | null>(null);

  const recarregar = useCallback(() => {
    setLista(null);
    const q = status ? `?status=${status}` : "";
    void api
      .get<LinhaOrcamento[]>(`/orcamentos${q}`)
      .then(setLista)
      .catch((e) => { aviso(e instanceof Error ? e.message : "falha ao listar"); setLista([]); });
  }, [status, aviso]);

  useEffect(recarregar, [recarregar]);

  return (
    <>
      <div className="cab-doc">
        <h1>Orçamentos</h1>
        <select
          className="campo-texto foco-claro" style={{ width: "auto" }}
          value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Situação"
        >
          <option value="">todos</option>
          <option value="ABERTO">abertos</option>
          <option value="CONVERTIDO">convertidos</option>
          <option value="CANCELADO">cancelados</option>
        </select>
        <span className="cod">{lista?.length ?? 0} documentos</span>
        <div style={{ marginLeft: "auto" }}>
          <button className="bt" onClick={aoNovo}>Novo orçamento</button>
        </div>
      </div>

      <div className="rolagem">
        <div className="painel">
          {lista === null ? (
            <Esqueleto />
          ) : lista.length === 0 ? (
            <div className="vazio">
              <p>Nenhum orçamento{status ? ` ${status.toLowerCase()}` : ""}.</p>
              <button className="bt" onClick={aoNovo}>Novo orçamento</button>
            </div>
          ) : (
            <table className="tab">
              <thead>
                <tr>
                  <th style={{ textAlign: "right" }}>Nº</th>
                  <th>Cliente</th>
                  <th>Emissão</th>
                  <th>Cubagem</th>
                  <th style={{ textAlign: "right" }}>Itens</th>
                  <th style={{ textAlign: "right" }}>Volume m³</th>
                  <th style={{ textAlign: "right" }}>Valor</th>
                  <th>Situação</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lista.map((o) => (
                  <tr key={o.id}>
                    <td className="r">{o.numero}</td>
                    <td>{o.parceiro}</td>
                    <td className="mono">{fmtData(o.criado_em)}</td>
                    <td><span className="pil p-info"><i />{o.regra_cubagem}</span></td>
                    <td className="r">{o.itens}</td>
                    <td className="r">{fmtVol(o.volume_total)}</td>
                    <td className="r">{fmtRs(o.valor_total)}</td>
                    <td>
                      {pilStatus(o.status)}
                      {o.pedido_numero !== null && (
                        <span className="cod" style={{ marginLeft: 6 }}>→ pedido {o.pedido_numero}</span>
                      )}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button
                        className="bt bt-neutro" style={{ padding: "3px 9px", fontSize: 12 }}
                        onClick={() => setAberto(aberto === o.id ? null : o.id)}
                      >
                        {aberto === o.id ? "fechar" : "abrir"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {aberto && (
          <DetalheOrcamento
            id={aberto}
            aviso={aviso}
            aoConverter={recarregar}
            aoDuplicar={aoDuplicar}
          />
        )}
      </div>
    </>
  );
}

function DetalheOrcamento({
  id, aviso, aoConverter, aoDuplicar,
}: {
  id: string;
  aviso: (t: string) => void;
  aoConverter: () => void;
  aoDuplicar: (itens: ItemDocumento[], parceiroId: string) => void;
}) {
  const [filial, setFilial] = useState<Filial | null>(null);
  useEffect(() => {
    void api.get<Filial[]>("/filiais")
      .then((f) => setFilial(f.find((x) => x.matriz) ?? f[0] ?? null))
      .catch(() => {});
  }, []);
  const [doc, setDoc] = useState<
    { numero: number; status: string; regraCubagem: string; parceiroId: string;
      volumeTotal: string; valorTotal: string; itens: ItemDocumento[] } | null
  >(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    setDoc(null);
    void api.get<typeof doc>(`/orcamentos/${id}`).then(setDoc).catch(() => setDoc(null));
  }, [id]);

  if (!doc) return <div className="painel"><Esqueleto /></div>;

  const emitente = {
    nome: filial?.razaoSocial ?? "—",
    cnpj: filial?.cnpj ?? "—",
    filial: filial?.nome ?? "—",
    cidade: filial?.cidade ?? "—",
    uf: filial?.uf ?? "—",
  };

  async function converter() {
    setOcupado(true);
    try {
      const p = await api.post<{ numero: number }>(`/orcamentos/${id}/converter`);
      aviso(`Convertido no pedido ${p.numero}`);
      aoConverter();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao converter");
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="painel">
      <div className="tt" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span className="rotulo">Orçamento {doc.numero}</span>
        {pilStatus(doc.status)}
        <span className="cod">cubagem {doc.regraCubagem}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <BotaoImprimir />
          <button
            className="bt bt-neutro" style={{ padding: "4px 10px", fontSize: 12 }}
            onClick={() => aoDuplicar(doc.itens, doc.parceiroId)}
          >
            Duplicar na grade
          </button>
          {doc.status === "ABERTO" && (
            <button
              className="bt" style={{ padding: "4px 10px", fontSize: 12 }}
              onClick={() => void converter()} disabled={ocupado}
            >
              Converter em pedido
            </button>
          )}
        </div>
      </div>
      <table className="tab">
        <thead>
          <tr>
            <th style={{ textAlign: "right" }}>#</th>
            <th>Bitola</th>
            <th style={{ textAlign: "right" }}>Peças</th>
            <th style={{ textAlign: "right" }}>Volume m³</th>
            <th style={{ textAlign: "right" }}>Preço/m³</th>
            <th style={{ textAlign: "right" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {doc.itens.map((i) => (
            <tr key={i.ordem}>
              <td className="r">{i.ordem}</td>
              <td className="mono">
                {i.espMm ? `${i.espMm} × ${i.largMm} × ${i.compM}` : "—"}
              </td>
              <td className="r">{i.pecas.toLocaleString("pt-BR")}</td>
              <td className="r">{fmtVol(i.volumeM3)}</td>
              <td className="r">{fmtRs(i.precoUnitario)}</td>
              <td className="r">{fmtRs(i.valorTotal)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 600, background: "#f4f6f7" }}>
            <td colSpan={3}>{doc.itens.length} itens</td>
            <td className="r">{fmtVol(doc.volumeTotal)}</td>
            <td />
            <td className="r">{fmtRs(doc.valorTotal)}</td>
          </tr>
        </tfoot>
      </table>

      {/* Some na tela, aparece no papel. Mesmo React, um layout só (§7). */}
      <Impresso
        emitente={emitente}
        titulo="Orçamento"
        numero={String(doc.numero)}
        emissao={new Date().toLocaleDateString("pt-BR")}
        rodapeEsquerda={`Cubagem: ${doc.regraCubagem}`}
        rodapeDireita={`Situação: ${situacaoImpressa(doc.status)}`}
        assinaturas={["Conferente", "Cliente"]}
      >
        <table>
          <thead>
            <tr>
              <th>#</th><th>Bitola</th>
              <th style={{ textAlign: "right" }}>Peças</th>
              <th style={{ textAlign: "right" }}>Volume m³</th>
              <th style={{ textAlign: "right" }}>Preço/m³</th>
              <th style={{ textAlign: "right" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {doc.itens.map((i) => (
              <tr key={i.ordem}>
                <td className="n">{i.ordem}</td>
                <td>{i.espMm ? `${i.espMm} × ${i.largMm} × ${i.compM}` : "—"}</td>
                <td className="n">{i.pecas.toLocaleString("pt-BR")}</td>
                <td className="n">{fmtVol(i.volumeM3)}</td>
                <td className="n">{fmtRs(i.precoUnitario)}</td>
                <td className="n">{fmtRs(i.valorTotal)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>{doc.itens.length} itens</td>
              <td className="n">{fmtVol(doc.volumeTotal)}</td>
              <td />
              <td className="n">{fmtRs(doc.valorTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </Impresso>
    </div>
  );
}

/* ------------------------------------------------------------- pedidos */

export function Pedidos({ aviso }: { aviso: (t: string) => void }) {
  const [lista, setLista] = useState<LinhaPedido[] | null>(null);
  const [status, setStatus] = useState("");

  const recarregar = useCallback(() => {
    setLista(null);
    const q = status ? `?status=${status}` : "";
    void api
      .get<LinhaPedido[]>(`/pedidos${q}`)
      .then(setLista)
      .catch(() => setLista([]));
  }, [status]);

  useEffect(recarregar, [recarregar]);

  async function faturar(id: string) {
    try {
      const r = await api.post<{ numero: number; chave: string; valorTributos: string }>(
        `/pedidos/${id}/faturar`, {},
      );
      // A baixa de estoque acontece AQUI, não no pedido (spec §2.7).
      aviso(
        `NF-e ${r.numero} autorizada · tributos R$ ${Number(r.valorTributos).toFixed(2)} · veja em Notas fiscais`,
      );
      recarregar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao faturar");
    }
  }

  async function cancelar(id: string, numero: number) {
    try {
      await api.post(`/pedidos/${id}/cancelar`);
      // A reserva some da projeção sem lançamento de estorno: pedido nunca
      // movimentou a razão (spec §2.7).
      aviso(`Pedido ${numero} cancelado · a reserva foi liberada`);
      recarregar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao cancelar");
    }
  }

  return (
    <>
      <div className="cab-doc">
        <h1>Pedidos</h1>
        <select
          className="campo-texto foco-claro" style={{ width: "auto" }}
          value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Situação"
        >
          <option value="">todos</option>
          <option value="ABERTO">abertos</option>
          <option value="FATURADO">faturados</option>
          <option value="CANCELADO">cancelados</option>
        </select>
        <span className="cod">pedido aberto reserva pátio, mas não movimenta a razão</span>
      </div>

      <div className="rolagem">
        <div className="painel">
          {lista === null ? (
            <Esqueleto />
          ) : lista.length === 0 ? (
            <div className="vazio"><p>Nenhum pedido{status ? ` ${status.toLowerCase()}` : ""}.</p></div>
          ) : (
            <table className="tab">
              <thead>
                <tr>
                  <th style={{ textAlign: "right" }}>Nº</th>
                  <th>Cliente</th>
                  <th>Emissão</th>
                  <th style={{ textAlign: "right" }}>Itens</th>
                  <th style={{ textAlign: "right" }}>Volume m³</th>
                  <th style={{ textAlign: "right" }}>Valor</th>
                  <th>Origem</th>
                  <th>Situação</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lista.map((p) => (
                  <tr key={p.id}>
                    <td className="r">{p.numero}</td>
                    <td>{p.parceiro}</td>
                    <td className="mono">{fmtData(p.criado_em)}</td>
                    <td className="r">{p.itens}</td>
                    <td className="r">{fmtVol(p.volume_total)}</td>
                    <td className="r">{fmtRs(p.valor_total)}</td>
                    <td className="cod">
                      {p.orcamento_numero !== null ? `orçamento ${p.orcamento_numero}` : "—"}
                    </td>
                    <td>{pilStatus(p.status)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {p.status === "ABERTO" && (
                        <span style={{ display: "inline-flex", gap: 6 }}>
                          <button
                            className="bt" style={{ padding: "3px 9px", fontSize: 12 }}
                            onClick={() => void faturar(p.id)}
                          >
                            faturar
                          </button>
                          <button
                            className="bt bt-neutro" style={{ padding: "3px 9px", fontSize: 12 }}
                            onClick={() => void cancelar(p.id, p.numero)}
                          >
                            cancelar
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

function Esqueleto() {
  return (
    <div className="esq">
      {[0, 1, 2, 3, 4].map((i) => (
        <div className="l" key={i}>
          <span className="b" /><span className="b" /><span className="b" /><span className="b" />
        </div>
      ))}
    </div>
  );
}

export type { ItemDocumento };
